import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi, useLiveData } from "../api/api";
import MetricsChart from "../api/MetricsChart";
import {
  AlertBanner,
  DescribeRow,
  PageTitle,
  Panel,
  StatTile,
  StatusChip,
} from "../api/controlPlaneUi";
import {
  FileChange,
  formatCost,
  Run,
  RunStatus,
  Stage,
  STAGES,
  STAGE_COLOR,
  STAGE_LABEL,
  StageLog,
  StageMetric,
  tokensOf,
} from "../api/types";

// ---------------------------------------------------------------------------
// Diff engine — pure LCS, no external library
// ---------------------------------------------------------------------------
type DiffOp = { type: "context" | "added" | "removed"; text: string };

function computeLineDiff(oldText: string, newText: string): DiffOp[] {
  const a = oldText.split("\n");
  const b = newText.split("\n");
  // Cap at 800 lines each to keep O(m*n) reasonable
  if (a.length > 800 || b.length > 800) {
    return b.map((text) => ({ type: "added", text }));
  }
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0)
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const ops: DiffOp[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      ops.unshift({ type: "context", text: a[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.unshift({ type: "added", text: b[j - 1] });
      j--;
    } else {
      ops.unshift({ type: "removed", text: a[i - 1] });
      i--;
    }
  }
  return ops;
}

// Collapse unchanged runs to ±3 context lines around each hunk.
function collapseContext(ops: DiffOp[], ctx = 3): DiffOp[] {
  const changed = new Set<number>();
  ops.forEach((op, i) => { if (op.type !== "context") changed.add(i); });
  const keep = new Set<number>();
  changed.forEach((ci) => {
    for (let d = -ctx; d <= ctx; d++) {
      const idx = ci + d;
      if (idx >= 0 && idx < ops.length) keep.add(idx);
    }
  });
  if (keep.size === 0) return [];
  const result: DiffOp[] = [];
  let skipped = 0;
  for (let i = 0; i < ops.length; i++) {
    if (keep.has(i)) {
      if (skipped > 0) {
        result.push({ type: "context", text: `@@ … ${skipped} unchanged line${skipped > 1 ? "s" : ""} … @@` });
        skipped = 0;
      }
      result.push(ops[i]);
    } else {
      skipped++;
    }
  }
  if (skipped > 0)
    result.push({ type: "context", text: `@@ … ${skipped} unchanged line${skipped > 1 ? "s" : ""} … @@` });
  return result;
}

// ---------------------------------------------------------------------------
// FileRow — single expandable file in the change-set panel
// ---------------------------------------------------------------------------
const STATUS_COLOR: Record<string, string> = {
  A: "bg-emerald-950/40 text-emerald-400",
  M: "bg-amber-950/40 text-amber-400",
  D: "bg-red-950/40 text-red-400",
};

const FileRow: React.FC<{ file: FileChange }> = ({ file }) => {
  const [open, setOpen] = useState(false);

  const ops = useMemo<DiffOp[]>(() => {
    if (file.status === "D") {
      const lines = (file.oldContent ?? "").split("\n");
      return lines.map((text) => ({ type: "removed", text }));
    }
    if (!file.content) return [];
    if (file.status === "A" || !file.oldContent) {
      return file.content.split("\n").map((text) => ({ type: "added" as const, text }));
    }
    return computeLineDiff(file.oldContent, file.content);
  }, [file]);

  const collapsed = useMemo(() => collapseContext(ops), [ops]);

  const stats = useMemo(() => {
    const added = ops.filter((o) => o.type === "added").length;
    const removed = ops.filter((o) => o.type === "removed").length;
    return { added, removed };
  }, [ops]);

  return (
    <div className="border border-[#262420] rounded-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left bg-[#141210] hover:bg-[#262420] transition-colors"
      >
        <span
          className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded font-mono ${STATUS_COLOR[file.status] ?? "bg-[#262420] text-[#9D9585]"}`}
        >
          {file.status}
        </span>
        <span className="font-mono text-xs text-slate-700 flex-1 truncate">{file.path}</span>
        {stats.added > 0 && (
          <span className="text-[11px] font-mono text-emerald-600">+{stats.added}</span>
        )}
        {stats.removed > 0 && (
          <span className="text-[11px] font-mono text-red-500">−{stats.removed}</span>
        )}
        <svg
          className={`h-3.5 w-3.5 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20" fill="currentColor" aria-hidden
        >
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className="overflow-x-auto">
          {collapsed.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-400 font-mono italic">
              {file.status === "D" ? "File deleted — no content." : "No content."}
            </p>
          ) : (
            <table className="w-full text-xs font-mono border-collapse">
              <tbody>
                {collapsed.map((op, idx) => {
                  const isHunk = op.text.startsWith("@@ …");
                  if (isHunk) {
                    return (
                      <tr key={idx} className="bg-[#262420]">
                        <td colSpan={2} className="px-3 py-0.5 text-slate-500 select-none">
                          {op.text}
                        </td>
                      </tr>
                    );
                  }
                  const bg =
                    op.type === "added"
                      ? "bg-emerald-950/40"
                      : op.type === "removed"
                      ? "bg-red-950/40"
                      : "";
                  const gutter =
                    op.type === "added"
                      ? "text-emerald-400 bg-emerald-950/60 select-none"
                      : op.type === "removed"
                      ? "text-red-400 bg-red-950/60 select-none"
                      : "text-[#5A5448] select-none";
                  const sign =
                    op.type === "added" ? "+" : op.type === "removed" ? "−" : " ";
                  return (
                    <tr key={idx} className={`${bg} leading-5`}>
                      <td className={`w-6 text-center pr-2 pl-1 ${gutter}`}>{sign}</td>
                      <td className="pr-4 text-[#E5E0D5] whitespace-pre">{op.text}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const formatNumber = (n: number): string => n.toLocaleString("en-US");
const formatSeconds = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;
const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};
const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString();
};
const shortId = (id: string): string => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

const LEVEL_DOT: Record<StageLog["level"], string> = {
  info: "bg-slate-400",
  warn: "bg-amber-500",
  error: "bg-red-500",
};

const isLiveStatus = (s: RunStatus): boolean =>
  s === "running" || s === "needs_rework";

const isLoopBackLog = (log: StageLog): boolean => {
  if (log.level !== "error") return false;
  const msg = log.message.toLowerCase();
  return msg.includes("returned fail") || msg.includes("looping back");
};

const RunDetailsPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const api = useApi();

  const fetchRun = useCallback(() => api.getRun(id), [api, id]);
  const {
    data: runData,
    error: runError,
    loading: runLoading,
    refresh: refreshRun,
  } = useLiveData<Run>(fetchRun, [fetchRun, id]);

  const [prBusy, setPrBusy] = useState(false);
  const [prError, setPrError] = useState<string | null>(null);
  const [changeSet, setChangeSet] = useState<FileChange[] | null>(null);
  const [csLoading, setCsLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setCsLoading(true);
    api.getChangeSet(id)
      .then(setChangeSet)
      .catch(() => setChangeSet(null))
      .finally(() => setCsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMetrics = useCallback(
    () => api.getMetrics({ runId: id }),
    [api, id]
  );
  const { data: metricsData } = useLiveData<StageMetric[]>(
    fetchMetrics,
    [fetchMetrics, id]
  );

  const fetchLogs = useCallback(
    () => api.getLogs({ runId: id, limit: 500 }),
    [api, id]
  );
  const { data: logsData } = useLiveData<StageLog[]>(fetchLogs, [fetchLogs, id]);

  const metrics = useMemo(() => metricsData ?? [], [metricsData]);
  const logs = useMemo(() => logsData ?? [], [logsData]);

  const summary = useMemo(() => {
    const totalTokens = metrics.reduce((acc, m) => acc + tokensOf(m), 0);
    const totalCost = metrics.reduce((acc, m) => acc + (m.costUsd ?? 0), 0);
    const totalDuration = metrics.reduce((acc, m) => acc + m.durationMs, 0);
    const hasActual = metrics.some((m) => m.actualCostUsd != null);
    const totalActualCost = hasActual
      ? metrics.reduce((acc, m) => acc + (m.actualCostUsd ?? m.costUsd ?? 0), 0)
      : null;
    return { totalTokens, totalCost, totalActualCost, totalDuration, count: metrics.length };
  }, [metrics]);

  const reworkEvents = useMemo(
    () =>
      logs
        .filter(isLoopBackLog)
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
    [logs]
  );

  const logsByStage = useMemo(() => {
    const groups = new Map<Stage, StageLog[]>();
    for (const log of logs) {
      const arr = groups.get(log.stage);
      if (arr) arr.push(log);
      else groups.set(log.stage, [log]);
    }
    groups.forEach((arr) =>
      arr.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
    );
    return STAGES.map((stage) => ({
      stage,
      logs: groups.get(stage) ?? [],
    })).filter((g) => g.logs.length > 0);
  }, [logs]);

  if (runLoading && !runData) {
    return (
      <Panel>
        <p className="text-sm text-slate-500 font-mono">Loading run resource…</p>
      </Panel>
    );
  }

  if (!runData) {
    return (
      <div className="space-y-4">
        <Link
          to="/orchestration/runs"
          className="text-sm text-[#C9A260] hover:underline font-mono"
        >
          ← tasks
        </Link>
        <AlertBanner variant="error">
          {runError
            ? `Could not load run ${shortId(id)}: ${runError}`
            : `Run ${shortId(id)} not found.`}
        </AlertBanner>
      </div>
    );
  }

  const run = runData;
  const live = isLiveStatus(run.status);

  // "Open PR" is available for a completed run that targets a repo and has no PR
  // yet. The PR is opened server-side in code via the scoped GitHub App.
  const canOpenPr = run.status === "completed" && !run.prUrl && !!run.targetRepo;
  const openPr = async () => {
    setPrBusy(true);
    setPrError(null);
    try {
      await api.openRunPr(run.id);
      refreshRun();
    } catch (e) {
      setPrError(e instanceof Error ? e.message : String(e));
    } finally {
      setPrBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
        <Link to="/orchestration/runs" className="text-[#C9A260] hover:underline">
          tasks
        </Link>
        <span>/</span>
        <span className="text-slate-700">{shortId(run.id)}</span>
      </div>

      <PageTitle
        kind="Run"
        name={run.title || run.storySlug || shortId(run.id)}
        status={run.status}
        pulse={live}
      >
        {run.prUrl ? (
          <a
            href={run.prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-mono text-[#C9A260] hover:underline"
          >
            pr ↗
          </a>
        ) : (
          canOpenPr && (
            <button
              type="button"
              onClick={openPr}
              disabled={prBusy}
              className="h-8 px-3 rounded-none border border-[#C9A260] text-[#C9A260] text-sm hover:bg-[#B8913F] hover:text-[#111110] disabled:opacity-50 transition-colors"
            >
              {prBusy ? "Opening PR…" : "Open PR"}
            </button>
          )
        )}
      </PageTitle>

      {prError && (
        <AlertBanner variant="error">Open PR failed: {prError}</AlertBanner>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Tokens" value={formatNumber(summary.totalTokens)} />
        <StatTile label="Cost (list)" value={formatCost(summary.totalCost)} />
        {summary.totalActualCost != null && (
          <StatTile label="Cost (billed)" value={formatCost(summary.totalActualCost)} />
        )}
        <StatTile label="Duration" value={formatSeconds(summary.totalDuration)} />
        <StatTile label="Samples" value={formatNumber(summary.count)} />
      </div>

      <Panel title="Metadata">
        <DescribeRow label="Name" value={run.title || "(untitled)"} />
        <DescribeRow label="UID" value={run.id} />
        <DescribeRow label="Story" value={run.storySlug} />
        <DescribeRow
          label="Status"
          value={<StatusChip status={run.status} pulse={live} />}
        />
        <DescribeRow label="Iterations" value={String(run.iterations)} />
        <DescribeRow label="Target repo" value={run.targetRepo || "—"} />
        <DescribeRow label="Domain" value={run.domain || "—"} />
        <DescribeRow label="Triggered by" value={run.triggeredBy || "— (machine / ad-hoc)"} />
        <DescribeRow label="Created" value={formatDate(run.createdAt)} />
        <DescribeRow label="Completed" value={formatDate(run.completedAt)} />
        <DescribeRow
          label="PR"
          value={
            run.prUrl ? (
              <a
                href={run.prUrl}
                className="text-[#C9A260] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {run.prUrl}
              </a>
            ) : (
              "—"
            )
          }
        />
      </Panel>

      {(csLoading || (changeSet && changeSet.length > 0)) && (
        <Panel
          title={
            csLoading
              ? "Change set — loading…"
              : `Change set (${changeSet!.length} file${changeSet!.length !== 1 ? "s" : ""})`
          }
        >
          {csLoading ? (
            <p className="text-xs text-slate-400 font-mono">Loading…</p>
          ) : (
            <div className="space-y-1">
              {changeSet!.map((f) => (
                <FileRow key={f.path} file={f} />
              ))}
            </div>
          )}
        </Panel>
      )}

      <Panel title="Metrics — per agent">
        <MetricsChart metrics={metrics} groupBy="agent" />
      </Panel>

      <Panel title={`Events — rework (${reworkEvents.length})`}>
        {reworkEvents.length === 0 ? (
          <p className="text-sm text-emerald-700 font-mono">
            No rework events — passed on first iteration.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-[#262420] rounded-sm">
            {reworkEvents.map((log, i) => (
              <li
                key={log.id}
                className="flex gap-3 px-3 py-2 text-sm bg-amber-50/50"
              >
                <span className="font-mono text-slate-400 w-6">{i + 1}</span>
                <span
                  className="h-2 w-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: STAGE_COLOR[log.stage] }}
                />
                <div className="min-w-0">
                  <p className="font-mono text-amber-900">
                    {STAGE_LABEL[log.stage]} → {STAGE_LABEL.analyze}
                  </p>
                  <p className="text-slate-700 mt-0.5 break-words">{log.message}</p>
                  <p className="text-xs text-slate-500 mt-1 font-mono">
                    {formatTime(log.timestamp)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title={`Logs (${logs.length})`}>
        {logsByStage.length === 0 ? (
          <p className="text-sm text-slate-500 font-mono">No log entries.</p>
        ) : (
          <div className="space-y-4">
            {logsByStage.map(({ stage, logs: stageLogs }) => (
              <div key={stage}>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: STAGE_COLOR[stage] }}
                  />
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    {STAGE_LABEL[stage]}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    ({stageLogs.length})
                  </span>
                </div>
                <ul className="border border-[#262420] rounded-sm divide-y divide-slate-100 font-mono text-xs">
                  {stageLogs.map((log) => (
                    <li
                      key={log.id}
                      className="flex gap-2 px-3 py-1.5 hover:bg-white/4"
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full mt-1 ${LEVEL_DOT[log.level]}`}
                      />
                      <span className="text-slate-400 w-16 flex-shrink-0">
                        {formatTime(log.timestamp)}
                      </span>
                      <span className="text-slate-700 break-words">{log.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default RunDetailsPage;